import { jwtDecode } from 'jwt-decode';

export interface DecodedToken {
  exp?: number;
  email?: string;
  upn?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  /** singular string or array — used by some identity providers */
  role?: string | string[];
  /** plural array of objects — used by Maersk's identity provider */
  roles?: Array<{ role: string; description?: string } | string> | string;
  [key: string]: unknown;
}

export function decodeToken(token: string): DecodedToken | null {
  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string | null | undefined): boolean {
  if (!token) return true;
  const decoded = decodeToken(token);
  if (!decoded?.exp) return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return decoded.exp <= nowSeconds;
}

/**
 * Extracts role names from a JWT token.
 *
 * Handles multiple shapes emitted by different identity providers:
 *   1. `role: "CustomerOnboardingAdmin"` — plain string (singular claim)
 *   2. `role: ["CustomerOnboardingAdmin", ...]` — array of strings
 *   3. `role: ["{\"role\":\"CustomerOnboardingAdmin\"}"]` — JSON-serialised objects (ASP.NET Core)
 *   4. `roles: [{"role":"CustomerOnboardingAdmin","description":"..."}]` — array of objects (Maersk IdP)
 *   5. `roles: "CustomerOnboardingAdmin"` — plain string on plural claim
 */
export function getUserRoles(token: string): string[] {
  const decoded = decodeToken(token);
  if (!decoded) return [];

  const raw: unknown = decoded['roles'] ?? decoded['role'];
  if (!raw) return [];

  const values: unknown[] = Array.isArray(raw) ? raw : [raw];

  return values.flatMap((v) => {
    if (v !== null && typeof v === 'object' && 'role' in (v as object)) {
      const nested = (v as Record<string, unknown>)['role'];
      return typeof nested === 'string' ? [nested] : [];
    }

    if (typeof v !== 'string') return [];

    try {
      const parsed: unknown = JSON.parse(v);
      if (parsed !== null && typeof parsed === 'object' && 'role' in (parsed as object)) {
        const nested = (parsed as Record<string, unknown>)['role'];
        return typeof nested === 'string' ? [nested] : [];
      }
    } catch {
      // not JSON — treat as plain role string
    }

    return [v];
  });
}
